import React, {createContext, useContext, useEffect, useState} from 'react';
import {useAuth} from '../providers/AuthProvider';
import {colors} from '../theme/colors';

const DatabaseContext = createContext();

export const useDatabase = () => {
  const context = useContext(DatabaseContext);
  if (!context) {
    throw new Error('useDatabase must be used within DatabaseProvider');
  }
  return context;
};

// Using localStorage for web PWA
const STORAGE_KEYS = {
  FOODS: '@calpro_foods',
  ENTRIES: '@calpro_entries',
  LAST_RESET_DATE: '@calpro_last_reset_date',
  SHOPPING_LIST: '@calpro_shopping_list',
  WATER_TRACKER: '@calpro_water_tracker',
};

// Helper functions to make localStorage async-compatible with error handling
const storageGetItem = (key) => {
  try {
    if (typeof Storage === 'undefined') {
      console.error('localStorage is not available');
      return Promise.resolve(null);
    }
    const value = localStorage.getItem(key);
    return Promise.resolve(value);
  } catch (error) {
    console.error(`Error reading from localStorage (${key}):`, error);
    return Promise.resolve(null);
  }
};

const storageSetItem = (key, value) => {
  try {
    if (typeof Storage === 'undefined') {
      console.error('localStorage is not available');
      return Promise.resolve(false);
    }
    localStorage.setItem(key, value);
    return Promise.resolve(true);
  } catch (error) {
    console.error(`Error writing to localStorage (${key}):`, error);
    // Check if it's a quota exceeded error
    if (error.name === 'QuotaExceededError') {
      console.error('localStorage quota exceeded. Please clear some data.');
    }
    return Promise.resolve(false);
  }
};

// Verify that data was saved correctly
const verifySave = async (key, expectedData) => {
  try {
    const saved = await storageGetItem(key);
    const expected = JSON.stringify(expectedData);
    if (saved !== expected) {
      console.error(`localStorage save verification failed for ${key}`);
      console.error('Expected:', expected);
      console.error('Got:', saved);
      return false;
    }
    return true;
  } catch (error) {
    console.error(`Error verifying save for ${key}:`, error);
    return false;
  }
};

export const DatabaseProvider = ({children}) => {
  const {user} = useAuth();
  const [isReady, setIsReady] = useState(false);
  const [foods, setFoods] = useState([]);
  const [entries, setEntries] = useState([]);

  // Get user-specific storage keys
  const getStorageKeys = () => {
    const userId = user?.id || 'anonymous';
    return {
      FOODS: `${STORAGE_KEYS.FOODS}_${userId}`,
      ENTRIES: `${STORAGE_KEYS.ENTRIES}_${userId}`,
      LAST_RESET_DATE: `${STORAGE_KEYS.LAST_RESET_DATE}_${userId}`,
      SHOPPING_LIST: `${STORAGE_KEYS.SHOPPING_LIST}_${userId}`,
      WATER_TRACKER: `${STORAGE_KEYS.WATER_TRACKER}_${userId}`,
    };
  };

  useEffect(() => {
    if (user) {
      initializeData();
    } else {
      setIsReady(true);
    }
  }, [user]);

  // Translation map for common English food names to Hebrew
  const englishToHebrewMap = {
    'apple': 'תפוח',
    'banana': 'בננה',
    'chicken breast': 'חזה עוף',
    'rice (cooked)': 'אורז (מבושל)',
    'rice': 'אורז',
    'egg': 'ביצה',
    'tuna in water': 'טונה במים',
    'tuna': 'טונה',
    'cottage cheese': 'גבינת קוטג\'',
    'light pita': 'פיתה קלה',
    'pita': 'פיתה',
    'myokal (light mayo)': 'מיוקל (מיונז קל)',
    'myokal': 'מיוקל',
    'potato / sweet potato': 'תפוח אדמה / בטטה',
    'potato': 'תפוח אדמה',
    'sweet potato': 'בטטה',
    'protein pudding': 'פודינג חלבון',
    'persian rice (cooked)': 'אורז פרסי (מבושל)',
    'persian rice': 'אורז פרסי',
    'yellow cheese 9%': 'גבינה צהובה 9%',
    'yellow cheese': 'גבינה צהובה',
    'sausage': 'נקניק',
    'tortilla': 'טורטיה',
  };

  // Check if a string contains Hebrew characters
  const containsHebrew = (str) => {
    return /[\u0590-\u05FF]/.test(str);
  };

  // Clean up duplicates: remove English versions if Hebrew exists, translate English-only foods
  const cleanupEnglishHebrewDuplicates = async (foodsList) => {
    if (!Array.isArray(foodsList) || foodsList.length === 0) {
      return foodsList;
    }

    const cleanedFoods = [];
    const hebrewNames = new Set();
    const englishNames = new Map(); // Map English name -> food object

    // First pass: separate Hebrew and English foods
    for (const food of foodsList) {
      const name = food.name.trim();
      if (containsHebrew(name)) {
        hebrewNames.add(name.toLowerCase());
        cleanedFoods.push(food);
      } else {
        const lowerName = name.toLowerCase();
        // Store English food, but we'll check if Hebrew version exists
        if (!englishNames.has(lowerName)) {
          englishNames.set(lowerName, food);
        }
      }
    }

    // Second pass: handle English foods
    for (const [englishName, englishFood] of englishNames.entries()) {
      // Check if there's a Hebrew translation for this English name
      const hebrewTranslation = englishToHebrewMap[englishName];
      
      if (hebrewTranslation) {
        // Check if Hebrew version already exists
        const hebrewExists = hebrewNames.has(hebrewTranslation.toLowerCase());
        
        if (hebrewExists) {
          // Hebrew version exists, skip English (don't add it)
          console.log(`[DatabaseContext] Removing English duplicate: "${englishFood.name}" (Hebrew version exists: "${hebrewTranslation}")`);
          continue;
        } else {
          // Hebrew version doesn't exist, translate the English food
          console.log(`[DatabaseContext] Translating "${englishFood.name}" to "${hebrewTranslation}"`);
          const translatedFood = {
            ...englishFood,
            name: hebrewTranslation,
          };
          cleanedFoods.push(translatedFood);
          hebrewNames.add(hebrewTranslation.toLowerCase());
        }
      } else {
        // No translation available, keep English version
        cleanedFoods.push(englishFood);
      }
    }

    return cleanedFoods;
  };

  // Seed predefined base foods into the database
  const seedBaseFoods = async (existingFoods) => {
    const predefinedFoods = [
      {
        name: 'טונה במים',
        base_amount: 1,
        base_amount_unit: 'unit',
        calories: 104,
        protein: 25,
        carbs: 0,
        fat: 0,
      },
      {
        name: 'חזה עוף',
        base_amount: 100,
        base_amount_unit: 'gram',
        calories: 165,
        protein: 31,
        carbs: 0,
        fat: 3,
      },
      {
        name: 'גבינת קוטג\'',
        base_amount: 1,
        base_amount_unit: 'tablespoon',
        calories: 19,
        protein: 2.20,
        carbs: 1,
        fat: 0,
      },
      {
        name: 'פיתה קלה',
        base_amount: 1,
        base_amount_unit: 'unit',
        calories: 99,
        protein: 3.50,
        carbs: 20,
        fat: 1,
      },
      {
        name: 'ביצה',
        base_amount: 1,
        base_amount_unit: 'unit',
        calories: 97,
        protein: 8.16,
        carbs: 0,
        fat: 6,
      },
      {
        name: 'מיוקל (מיונז קל)',
        base_amount: 1,
        base_amount_unit: 'tablespoon',
        calories: 27,
        protein: 0,
        carbs: 3,
        fat: 1,
      },
      {
        name: 'תפוח אדמה / בטטה',
        base_amount: 100,
        base_amount_unit: 'gram',
        calories: 78,
        protein: 1,
        carbs: 17,
        fat: 0,
      },
      {
        name: 'פודינג חלבון',
        base_amount: 1,
        base_amount_unit: 'unit',
        calories: 112,
        protein: 20,
        carbs: 0,
        fat: 0,
      },
      {
        name: 'אורז פרסי (מבושל)',
        base_amount: 100,
        base_amount_unit: 'gram',
        calories: 125,
        protein: 2,
        carbs: 28,
        fat: 0,
      },
      {
        name: 'גבינה צהובה 9%',
        base_amount: 1,
        base_amount_unit: 'unit',
        calories: 50,
        protein: 7.50,
        carbs: 0,
        fat: 2.25,
      },
      {
        name: 'טורטיה',
        base_amount: 1,
        base_amount_unit: 'unit',
        calories: 70,
        protein: 2.5,
        carbs: 13,
        fat: 1.5,
      },
    ];

    const existingNames = new Set(
      existingFoods.map(f => f.name.toLowerCase().trim())
    );
    let maxId = existingFoods.length > 0 
      ? Math.max(...existingFoods.map(f => f.id || 0)) 
      : 0;
    
    const foodsToAdd = [];
    for (const food of predefinedFoods) {
      const normalizedName = food.name.toLowerCase().trim();
      if (!existingNames.has(normalizedName)) {
        maxId += 1;
        foodsToAdd.push({
          id: maxId,
          ...food,
        });
        existingNames.add(normalizedName);
      }
    }

    if (foodsToAdd.length > 0) {
      console.log(`[DatabaseContext] Seeding ${foodsToAdd.length} new base foods`);
      const updatedFoods = [...existingFoods, ...foodsToAdd];
      const keys = getStorageKeys();
      const saved = await storageSetItem(keys.FOODS, JSON.stringify(updatedFoods));
      if (saved) {
        await verifySave(keys.FOODS, updatedFoods);
        return updatedFoods;
      }
    } else {
      console.log('[DatabaseContext] All base foods already exist, skipping seed');
    }
    
    return existingFoods;
  };

  const initializeData = async () => {
    try {
      console.log('[DatabaseContext] Initializing data...');
      
      // Load foods - CRITICAL: Only add sample foods if foods array is truly empty
      const keys = getStorageKeys();
      const foodsData = await storageGetItem(keys.FOODS);
      let currentFoods = [];
      
      if (foodsData) {
        try {
          const parsedFoods = JSON.parse(foodsData);
          if (parsedFoods && Array.isArray(parsedFoods) && parsedFoods.length > 0) {
            console.log(`[DatabaseContext] Loaded ${parsedFoods.length} foods from localStorage`);
            currentFoods = parsedFoods;
          } else {
            // Array exists but is empty - start with empty array, seed will add foods
            console.log('[DatabaseContext] Foods array is empty, will seed base foods');
            currentFoods = [];
          }
        } catch (parseError) {
          console.error('[DatabaseContext] Error parsing foods data:', parseError);
          // If parsing fails, start with empty array
          currentFoods = [];
        }
      } else {
        // No foods data exists - start with empty array, seed will add foods
        console.log('[DatabaseContext] No foods data found, will seed base foods');
        currentFoods = [];
      }

      // Clean up English/Hebrew duplicates before seeding
      const cleanedBefore = currentFoods.length;
      currentFoods = await cleanupEnglishHebrewDuplicates(currentFoods);
      const cleanedAfter = currentFoods.length;
      
      if (cleanedBefore !== cleanedAfter) {
        console.log(`[DatabaseContext] Cleaned up ${cleanedBefore - cleanedAfter} duplicate(s)`);
        // Save cleaned foods immediately
        await storageSetItem(keys.FOODS, JSON.stringify(currentFoods));
      }
      
      // Seed predefined base foods (will skip if already exist)
      currentFoods = await seedBaseFoods(currentFoods);
      
      // Save final foods list
      await storageSetItem(keys.FOODS, JSON.stringify(currentFoods));
      await verifySave(keys.FOODS, currentFoods);
      
      setFoods(currentFoods);

      // Load entries
      const entriesData = await storageGetItem(keys.ENTRIES);
      if (entriesData) {
        try {
          const parsedEntries = JSON.parse(entriesData);
          if (parsedEntries && Array.isArray(parsedEntries)) {
            console.log(`[DatabaseContext] Loaded ${parsedEntries.length} entries from localStorage`);
            setEntries(parsedEntries);
          } else {
            console.log('[DatabaseContext] Entries data is invalid, initializing empty array');
            setEntries([]);
          }
        } catch (parseError) {
          console.error('[DatabaseContext] Error parsing entries data:', parseError);
          setEntries([]);
        }
      } else {
        console.log('[DatabaseContext] No entries data found, initializing empty array');
        setEntries([]);
      }

      // Check for midnight reset - track last reset date
      const today = new Date().toISOString().split('T')[0];
      const lastReset = await storageGetItem(keys.LAST_RESET_DATE);
      
      if (lastReset !== today) {
        console.log(`[DatabaseContext] New day detected (last reset: ${lastReset}, today: ${today})`);
        // Update last reset date
        await storageSetItem(keys.LAST_RESET_DATE, today);
        // Note: We don't delete old entries - they're automatically filtered by date
        // in getTodayEntries() and getEntriesByDate(). This preserves history.
        console.log('[DatabaseContext] Reset date updated. Old entries remain in history.');
      } else {
        console.log('[DatabaseContext] Same day, no reset needed');
      }

      setIsReady(true);
      console.log('[DatabaseContext] Initialization complete');
    } catch (error) {
      console.error('[DatabaseContext] Error initializing data:', error);
      // Set default data on error - but don't overwrite existing foods
      const keys = getStorageKeys();
      const foodsData = await storageGetItem(keys.FOODS);
      let currentFoods = [];
      if (!foodsData) {
        // Start with empty array, seed will add foods
        currentFoods = [];
      } else {
        try {
          const parsed = JSON.parse(foodsData);
          if (Array.isArray(parsed)) {
            currentFoods = parsed;
          }
        } catch (e) {
          // Ignore parse errors
        }
      }
      // Try to clean up and seed base foods even on error
      try {
        currentFoods = await cleanupEnglishHebrewDuplicates(currentFoods);
        currentFoods = await seedBaseFoods(currentFoods);
        if (currentFoods.length > 0) {
          const keys = getStorageKeys();
      await storageSetItem(keys.FOODS, JSON.stringify(currentFoods));
        }
      } catch (seedError) {
        console.error('[DatabaseContext] Error cleaning/seeding base foods:', seedError);
      }
      setFoods(currentFoods);
      setEntries([]);
      setIsReady(true);
    }
  };

  const getAllFoods = async () => {
    try {
      console.log('[DatabaseContext] getAllFoods called');
      const keys = getStorageKeys();
      const foodsData = await storageGetItem(keys.FOODS);
      const foodsList = foodsData ? JSON.parse(foodsData) : [];
      if (Array.isArray(foodsList)) {
        setFoods(foodsList);
        console.log(`[DatabaseContext] Returning ${foodsList.length} foods`);
        return foodsList;
      }
      return foods;
    } catch (error) {
      console.error('[DatabaseContext] Error fetching foods:', error);
      return foods;
    }
  };

  const addFood = async (name, calories, protein, carbs, fat, baseAmount, baseAmountUnit) => {
    try {
      console.log(`[DatabaseContext] addFood called: ${name}`);
      const keys = getStorageKeys();
      const foodsData = await storageGetItem(keys.FOODS);
      let foodsList = foodsData ? JSON.parse(foodsData) : [];
      
      if (!Array.isArray(foodsList)) {
        console.error('[DatabaseContext] Foods list is not an array');
        return false;
      }
      
      // Clean up duplicates first
      const beforeCleanup = foodsList.length;
      foodsList = await cleanupEnglishHebrewDuplicates(foodsList);
      const afterCleanup = foodsList.length;
      
      // If cleanup removed duplicates, save immediately
      if (beforeCleanup !== afterCleanup) {
        console.log(`[DatabaseContext] Cleaned up ${beforeCleanup - afterCleanup} duplicate(s) during addFood`);
        await storageSetItem(keys.FOODS, JSON.stringify(foodsList));
      }
      
      const normalizedNewName = name.toLowerCase().trim();
      const newNameIsHebrew = containsHebrew(name);
      
      // Check if food with same name already exists (exact match)
      const exactMatch = foodsList.find(f => f.name.toLowerCase().trim() === normalizedNewName);
      if (exactMatch) {
        console.error(`[DatabaseContext] Food with this name already exists: "${exactMatch.name}"`);
        return false;
      }
      
      // If adding Hebrew food, check if English version exists and remove it
      if (newNameIsHebrew) {
        // Check if there's an English equivalent that should be removed
        for (const [englishName, hebrewName] of Object.entries(englishToHebrewMap)) {
          if (hebrewName.toLowerCase() === normalizedNewName) {
            // Found English equivalent, remove English version if exists
            const beforeCount = foodsList.length;
            foodsList = foodsList.filter(f => {
              const isEnglishVersion = !containsHebrew(f.name) && 
                                       f.name.toLowerCase().trim() === englishName;
              if (isEnglishVersion) {
                console.log(`[DatabaseContext] Removing English duplicate: "${f.name}" (adding Hebrew: "${name}")`);
              }
              return !isEnglishVersion;
            });
            if (foodsList.length < beforeCount) {
              console.log(`[DatabaseContext] Removed ${beforeCount - foodsList.length} English duplicate(s)`);
            }
            break;
          }
        }
      } else {
        // If adding English food, check if Hebrew version exists
        const hebrewTranslation = englishToHebrewMap[normalizedNewName];
        if (hebrewTranslation) {
          const hebrewExists = foodsList.some(f => 
            containsHebrew(f.name) && f.name.toLowerCase().trim() === hebrewTranslation.toLowerCase()
          );
          if (hebrewExists) {
            console.error(`[DatabaseContext] Hebrew version already exists: "${hebrewTranslation}"`);
            return false;
          }
        }
      }
      
      const newId = foodsList.length > 0 ? Math.max(...foodsList.map(f => f.id || 0)) + 1 : 1;
      
      // Use new schema if baseAmount and baseAmountUnit are provided
      const newFood = baseAmount !== undefined && baseAmountUnit
        ? {
            id: newId,
            name,
            base_amount: baseAmount,
            base_amount_unit: baseAmountUnit,
            calories,
            protein,
            carbs,
            fat,
          }
        : {
            // Fallback to old schema for backward compatibility
            id: newId,
            name,
            calories_per_100g: calories,
            protein_per_100g: protein,
            carbs_per_100g: carbs,
            fat_per_100g: fat,
          };
      
      const updatedFoods = [...foodsList, newFood];
      
      const saved = await storageSetItem(keys.FOODS, JSON.stringify(updatedFoods));
      if (!saved) {
        console.error('[DatabaseContext] Failed to save food to localStorage');
        return false;
      }
      
      // Verify the save
      const verified = await verifySave(STORAGE_KEYS.FOODS, updatedFoods);
      if (!verified) {
        console.error('[DatabaseContext] Food save verification failed');
        return false;
      }
      
      setFoods(updatedFoods);
      console.log(`[DatabaseContext] Food added successfully. Total foods: ${updatedFoods.length}`);
      return true;
    } catch (error) {
      console.error('[DatabaseContext] Error adding food:', error);
      return false;
    }
  };

  const addEntry = async (foodId, grams, calories, protein, carbs, fat) => {
    try {
      console.log(`[DatabaseContext] addEntry called: foodId=${foodId}, grams=${grams}`);
      const keys = getStorageKeys();
      const entriesData = await storageGetItem(keys.ENTRIES);
      const entriesList = entriesData ? JSON.parse(entriesData) : [];
      
      if (!Array.isArray(entriesList)) {
        console.error('[DatabaseContext] Entries list is not an array');
        return false;
      }
      
      const today = new Date().toISOString().split('T')[0];
      const newId = entriesList.length > 0 ? Math.max(...entriesList.map(e => e.id)) + 1 : 1;
      
      // Find food name
      const foodsData = await storageGetItem(keys.FOODS);
      const foodsList = foodsData ? JSON.parse(foodsData) : [];
      const food = foodsList.find(f => f.id === foodId);
      
      const newEntry = {
        id: newId,
        date: today,
        food_id: foodId,
        food_name: food ? food.name : 'Unknown',
        grams,
        calories,
        protein,
        carbs,
        fat,
      };
      const updatedEntries = [newEntry, ...entriesList];
      
      const saved = await storageSetItem(keys.ENTRIES, JSON.stringify(updatedEntries));
      if (!saved) {
        console.error('[DatabaseContext] Failed to save entry to localStorage');
        return false;
      }
      
      setEntries(updatedEntries);
      console.log(`[DatabaseContext] Entry added successfully. Total entries: ${updatedEntries.length}`);
      return true;
    } catch (error) {
      console.error('[DatabaseContext] Error adding entry:', error);
      return false;
    }
  };

  const getTodayEntries = async () => {
    try {
      const today = new Date().toISOString().split('T')[0];
      console.log(`[DatabaseContext] getTodayEntries called for date: ${today}`);
      const keys = getStorageKeys();
      const entriesData = await storageGetItem(keys.ENTRIES);
      const entriesList = entriesData ? JSON.parse(entriesData) : [];
      
      if (!Array.isArray(entriesList)) {
        return [];
      }
      
      // Get food names
      const foodsData = await storageGetItem(keys.FOODS);
      const foodsList = foodsData ? JSON.parse(foodsData) : [];
      const foodsMap = {};
      foodsList.forEach(f => {
        foodsMap[f.id] = f.name;
      });
      
      // Filter entries by today's date only
      const todayEntries = entriesList
        .filter(e => e.date === today)
        .map(e => ({
          ...e,
          food_name: e.food_name || foodsMap[e.food_id] || 'Unknown',
        }))
        .reverse();
      
      console.log(`[DatabaseContext] Returning ${todayEntries.length} entries for today`);
      return todayEntries;
    } catch (error) {
      console.error('[DatabaseContext] Error fetching today entries:', error);
      return [];
    }
  };

  const getEntriesByDate = async date => {
    try {
      console.log(`[DatabaseContext] getEntriesByDate called for date: ${date}`);
      const keys = getStorageKeys();
      const entriesData = await storageGetItem(keys.ENTRIES);
      const entriesList = entriesData ? JSON.parse(entriesData) : [];
      
      if (!Array.isArray(entriesList)) {
        return [];
      }
      
      // Get food names
      const foodsData = await storageGetItem(keys.FOODS);
      const foodsList = foodsData ? JSON.parse(foodsData) : [];
      const foodsMap = {};
      foodsList.forEach(f => {
        foodsMap[f.id] = f.name;
      });
      
      const dateEntries = entriesList
        .filter(e => e.date === date)
        .map(e => ({
          ...e,
          food_name: e.food_name || foodsMap[e.food_id] || 'Unknown',
        }))
        .reverse();
      
      console.log(`[DatabaseContext] Returning ${dateEntries.length} entries for date ${date}`);
      return dateEntries;
    } catch (error) {
      console.error('[DatabaseContext] Error fetching entries by date:', error);
      return [];
    }
  };

  const getAllDates = async () => {
    try {
      console.log('[DatabaseContext] getAllDates called');
      const keys = getStorageKeys();
      const entriesData = await storageGetItem(keys.ENTRIES);
      const entriesList = entriesData ? JSON.parse(entriesData) : [];
      
      if (!Array.isArray(entriesList)) {
        return [];
      }
      
      const dates = [...new Set(entriesList.map(e => e.date))].sort().reverse();
      console.log(`[DatabaseContext] Returning ${dates.length} unique dates`);
      return dates;
    } catch (error) {
      console.error('[DatabaseContext] Error fetching dates:', error);
      return [];
    }
  };

  const deleteEntry = async (entryId) => {
    try {
      console.log(`[DatabaseContext] deleteEntry called: entryId=${entryId}`);
      const keys = getStorageKeys();
      const entriesData = await storageGetItem(keys.ENTRIES);
      const entriesList = entriesData ? JSON.parse(entriesData) : [];
      
      if (!Array.isArray(entriesList)) {
        console.error('[DatabaseContext] Entries list is not an array');
        return false;
      }
      
      const updatedEntries = entriesList.filter(e => e.id !== entryId);
      
      if (updatedEntries.length === entriesList.length) {
        console.error(`[DatabaseContext] Entry with id ${entryId} not found`);
        return false;
      }
      
      const saved = await storageSetItem(keys.ENTRIES, JSON.stringify(updatedEntries));
      if (!saved) {
        console.error('[DatabaseContext] Failed to save entries after deletion');
        return false;
      }
      
      setEntries(updatedEntries);
      console.log(`[DatabaseContext] Entry deleted successfully. Remaining entries: ${updatedEntries.length}`);
      return true;
    } catch (error) {
      console.error('[DatabaseContext] Error deleting entry:', error);
      return false;
    }
  };

  // Shopping List Functions
  const getShoppingList = async () => {
    try {
      const keys = getStorageKeys();
      const listData = await storageGetItem(keys.SHOPPING_LIST);
      const list = listData ? JSON.parse(listData) : [];
      return Array.isArray(list) ? list : [];
    } catch (error) {
      console.error('[DatabaseContext] Error getting shopping list:', error);
      return [];
    }
  };

  const addShoppingItem = async (itemName) => {
    try {
      const keys = getStorageKeys();
      const listData = await storageGetItem(keys.SHOPPING_LIST);
      const list = listData ? JSON.parse(listData) : [];
      
      if (!Array.isArray(list)) {
        return false;
      }

      const newId = list.length > 0 ? Math.max(...list.map(i => i.id || 0)) + 1 : 1;
      const newItem = {
        id: newId,
        name: itemName,
        checked: false,
        createdAt: new Date().toISOString(),
      };

      const updatedList = [...list, newItem];
      const saved = await storageSetItem(keys.SHOPPING_LIST, JSON.stringify(updatedList));
      return saved;
    } catch (error) {
      console.error('[DatabaseContext] Error adding shopping item:', error);
      return false;
    }
  };

  const toggleShoppingItem = async (itemId) => {
    try {
      const keys = getStorageKeys();
      const listData = await storageGetItem(keys.SHOPPING_LIST);
      const list = listData ? JSON.parse(listData) : [];
      
      if (!Array.isArray(list)) {
        return false;
      }

      const updatedList = list.map(item => 
        item.id === itemId ? { ...item, checked: !item.checked } : item
      );

      const saved = await storageSetItem(keys.SHOPPING_LIST, JSON.stringify(updatedList));
      return saved;
    } catch (error) {
      console.error('[DatabaseContext] Error toggling shopping item:', error);
      return false;
    }
  };

  const deleteShoppingItem = async (itemId) => {
    try {
      const keys = getStorageKeys();
      const listData = await storageGetItem(keys.SHOPPING_LIST);
      const list = listData ? JSON.parse(listData) : [];
      
      if (!Array.isArray(list)) {
        return false;
      }

      const updatedList = list.filter(item => item.id !== itemId);
      const saved = await storageSetItem(keys.SHOPPING_LIST, JSON.stringify(updatedList));
      return saved;
    } catch (error) {
      console.error('[DatabaseContext] Error deleting shopping item:', error);
      return false;
    }
  };

  const clearCheckedShoppingItems = async () => {
    try {
      const keys = getStorageKeys();
      const listData = await storageGetItem(keys.SHOPPING_LIST);
      const list = listData ? JSON.parse(listData) : [];
      
      if (!Array.isArray(list)) {
        return false;
      }

      const updatedList = list.filter(item => !item.checked);
      const saved = await storageSetItem(keys.SHOPPING_LIST, JSON.stringify(updatedList));
      return saved;
    } catch (error) {
      console.error('[DatabaseContext] Error clearing checked items:', error);
      return false;
    }
  };

  // Water Tracker Functions
  const getWaterIntake = async (date) => {
    try {
      const keys = getStorageKeys();
      const waterData = await storageGetItem(keys.WATER_TRACKER);
      const waterList = waterData ? JSON.parse(waterData) : [];
      
      if (!Array.isArray(waterList)) {
        return 0;
      }

      const dateString = date || new Date().toISOString().split('T')[0];
      const dayEntry = waterList.find(entry => entry.date === dateString);
      return dayEntry ? dayEntry.amount : 0;
    } catch (error) {
      console.error('[DatabaseContext] Error getting water intake:', error);
      return 0;
    }
  };

  const addWaterIntake = async (amount, date) => {
    try {
      const keys = getStorageKeys();
      const waterData = await storageGetItem(keys.WATER_TRACKER);
      const waterList = waterData ? JSON.parse(waterData) : [];
      
      if (!Array.isArray(waterList)) {
        return false;
      }

      const dateString = date || new Date().toISOString().split('T')[0];
      const existingIndex = waterList.findIndex(entry => entry.date === dateString);

      let updatedList;
      if (existingIndex >= 0) {
        // Update existing entry
        updatedList = [...waterList];
        updatedList[existingIndex] = {
          ...updatedList[existingIndex],
          amount: (updatedList[existingIndex].amount || 0) + amount,
          updatedAt: new Date().toISOString(),
        };
      } else {
        // Create new entry
        const newEntry = {
          date: dateString,
          amount: amount,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
        updatedList = [...waterList, newEntry];
      }

      const saved = await storageSetItem(keys.WATER_TRACKER, JSON.stringify(updatedList));
      return saved;
    } catch (error) {
      console.error('[DatabaseContext] Error adding water intake:', error);
      return false;
    }
  };

  const setWaterIntake = async (amount, date) => {
    try {
      const keys = getStorageKeys();
      const waterData = await storageGetItem(keys.WATER_TRACKER);
      const waterList = waterData ? JSON.parse(waterData) : [];
      
      if (!Array.isArray(waterList)) {
        return false;
      }

      const dateString = date || new Date().toISOString().split('T')[0];
      const existingIndex = waterList.findIndex(entry => entry.date === dateString);

      let updatedList;
      if (existingIndex >= 0) {
        // Update existing entry
        updatedList = [...waterList];
        updatedList[existingIndex] = {
          ...updatedList[existingIndex],
          amount: amount,
          updatedAt: new Date().toISOString(),
        };
      } else {
        // Create new entry
        const newEntry = {
          date: dateString,
          amount: amount,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
        updatedList = [...waterList, newEntry];
      }

      const saved = await storageSetItem(keys.WATER_TRACKER, JSON.stringify(updatedList));
      return saved;
    } catch (error) {
      console.error('[DatabaseContext] Error setting water intake:', error);
      return false;
    }
  };

  const getWaterHistory = async () => {
    try {
      const keys = getStorageKeys();
      const waterData = await storageGetItem(keys.WATER_TRACKER);
      const waterList = waterData ? JSON.parse(waterData) : [];
      return Array.isArray(waterList) ? waterList : [];
    } catch (error) {
      console.error('[DatabaseContext] Error getting water history:', error);
      return [];
    }
  };

  const value = {
    isReady,
    getAllFoods,
    addFood,
    addEntry,
    deleteEntry,
    getTodayEntries,
    getEntriesByDate,
    getAllDates,
    getShoppingList,
    addShoppingItem,
    toggleShoppingItem,
    deleteShoppingItem,
    clearCheckedShoppingItems,
    getWaterIntake,
    addWaterIntake,
    setWaterIntake,
    getWaterHistory,
  };

  // Show loading state while initializing
  if (!isReady) {
    return (
      <div style={{
        flex: 1,
        backgroundColor: colors.bg,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: 20,
        minHeight: '100vh',
      }}>
        <div style={{textAlign: 'center'}}>
          <div style={{color: colors.primary, fontSize: 24, marginBottom: 10, fontWeight: 'bold'}}>CalPro</div>
          <div style={{color: colors.textPrimary, fontSize: 16}}>Loading...</div>
          <div style={{color: colors.textTertiary, fontSize: 12, marginTop: 10}}>Initializing app...</div>
        </div>
      </div>
    );
  }

  return (
    <DatabaseContext.Provider value={value}>
      {children}
    </DatabaseContext.Provider>
  );
};
